import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check, Globe, Link2, Lock, Zap, AlertTriangle } from 'lucide-react';
import {
  TierFeatureKey,
  academySubdomainLabelSchema,
  canAccessFeature,
  formatOrgSubdomainHost,
  isOrgBrandedSubdomainActive,
  orgPublicBaseUrl,
  type Organization,
} from '@court-sync/shared';
import { cn } from '@/lib/utils';
import { organizationService } from '@/services/organization';
import { Badge, Button } from '@/components/ui';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/AlertDialog';
import { AcademySettingsSection } from '@/components/academy/AcademySettingsSection';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { useSubscription } from '@/hooks/useSubscription';
import { useClubModuleAccess } from '@/hooks/useClubModuleAccess';

type PublicSiteMode = 'path' | 'subdomain';

interface OrgSubdomainSettingsProps {
  organization: Organization;
  slugValue: string;
  slugError?: string;
  onSlugChange: (slug: string) => void;
  onUpdated: (organization: Organization) => void;
}

interface PublicSiteModeCardProps {
  mode: PublicSiteMode;
  selected: boolean;
  active: boolean;
  disabled?: boolean;
  title: string;
  description: string;
  icon: ReactNode;
  badge?: ReactNode;
  onSelect: () => void;
  children?: ReactNode;
}

function PublicSiteModeCard({
  mode,
  selected,
  active,
  disabled = false,
  title,
  description,
  icon,
  badge,
  onSelect,
  children,
}: PublicSiteModeCardProps) {
  const inputId = useId();

  return (
    <div
      className={cn(
        'rounded-xl border-2 transition-colors',
        selected
          ? 'border-primary-500 bg-primary-50/80 shadow-sm dark:border-primary-500/50 dark:bg-slate-900/80 dark:shadow-none'
          : 'border-border bg-card hover:border-primary/40 dark:border-slate-700 dark:bg-slate-900/40 dark:hover:border-slate-600',
        disabled && 'opacity-60'
      )}
    >
      <label
        htmlFor={inputId}
        className={cn(
          'flex cursor-pointer items-start gap-3 p-4 sm:p-5',
          disabled && 'cursor-not-allowed'
        )}
      >
        <input
          id={inputId}
          type="radio"
          name="org-public-site-mode"
          value={mode}
          checked={selected}
          onChange={() => onSelect()}
          disabled={disabled}
          className="mt-1 h-4 w-4 shrink-0 border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1.5 font-medium text-gray-900 dark:text-white">
              {icon}
              {title}
            </span>
            {active ? (
              <Badge variant="success" className="font-medium">
                Active
              </Badge>
            ) : null}
            {badge}
          </span>
          <span className="mt-1 block text-sm text-gray-500 dark:text-gray-400">{description}</span>
        </span>
      </label>

      {selected && children ? (
        <div className="space-y-4 border-t border-border/80 px-4 pb-4 pt-4 dark:border-slate-700/80 sm:px-5 sm:pb-5">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function OrgSubdomainSettings({
  organization,
  slugValue,
  slugError,
  onSlugChange,
  onUpdated,
}: OrgSubdomainSettingsProps) {
  const { currentTier: ownerTier } = useSubscription();
  const { entitlements } = useClubModuleAccess();
  const tier = ownerTier ?? 'starter';
  const clubTier = entitlements?.clubTier ?? null;
  const hasAccess =
    canAccessFeature(tier as 'starter' | 'professional' | 'enterprise', TierFeatureKey.BRANDED_SUBDOMAIN) ||
    (clubTier
      ? canAccessFeature(clubTier as 'starter' | 'professional' | 'enterprise', TierFeatureKey.BRANDED_SUBDOMAIN)
      : false);

  const isSubdomainClaimed = Boolean(organization.subdomain?.trim());
  const [label, setLabel] = useState(organization.subdomain ?? organization.slug);
  const [enabled, setEnabled] = useState(organization.subdomainEnabled ?? false);
  const [checking, setChecking] = useState(false);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [showClaimConfirm, setShowClaimConfirm] = useState(false);

  const liveMode: PublicSiteMode = isOrgBrandedSubdomainActive(organization, tier, clubTier)
    ? 'subdomain'
    : 'path';

  const [mode, setMode] = useState<PublicSiteMode>(liveMode);

  const validateLabel = useCallback((value: string) => {
    const parsed = academySubdomainLabelSchema.safeParse(value.trim().toLowerCase());
    return parsed.success ? null : parsed.error.issues[0]?.message ?? 'Invalid label';
  }, []);

  useEffect(() => {
    if (!isSubdomainClaimed) {
      setLabel(organization.subdomain ?? organization.slug);
    }
    setEnabled(organization.subdomainEnabled ?? false);
    setMode(liveMode);
  }, [organization.subdomain, organization.slug, organization.subdomainEnabled, isSubdomainClaimed, liveMode]);

  useEffect(() => {
    if (!hasAccess || isSubdomainClaimed || mode !== 'subdomain') return undefined;
    const err = validateLabel(label);
    if (err) {
      setAvailable(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      setChecking(true);
      try {
        const result = await organizationService.checkSubdomainAvailability(
          label.trim().toLowerCase(),
          organization.id
        );
        setAvailable(result.data?.available ?? null);
      } catch {
        setAvailable(null);
      } finally {
        setChecking(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [label, organization.id, hasAccess, isSubdomainClaimed, mode, validateLabel]);

  const handleModeChange = (nextMode: PublicSiteMode) => {
    if (nextMode === 'subdomain' && !hasAccess) return;
    setMode(nextMode);
    setEnabled(nextMode === 'subdomain');
  };

  const saveSubdomain = async (subdomain: string, subdomainEnabled: boolean) => {
    const res = await organizationService.updateOrganization(organization.id, {
      subdomain: subdomain.trim().toLowerCase(),
      subdomainEnabled,
    });
    if (res.success && res.data) {
      onUpdated(res.data);
      return true;
    }
    toast.error(res.error || 'Failed to update subdomain settings');
    return false;
  };

  const handleClaim = async () => {
    const err = validateLabel(label);
    if (err) {
      toast.error(err);
      return;
    }
    if (available === false) {
      toast.error('This subdomain is already in use');
      return;
    }
    setSaving(true);
    try {
      const ok = await saveSubdomain(label, true);
      if (ok) {
        setShowClaimConfirm(false);
        toast.success('Branded subdomain is now your public site');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveMode = async () => {
    if (mode === 'subdomain' && !isSubdomainClaimed) {
      await handleClaim();
      return;
    }
    if (!organization.subdomain?.trim()) return;
    setSaving(true);
    try {
      const ok = await saveSubdomain(organization.subdomain, enabled);
      if (ok) {
        toast.success(
          enabled
            ? 'Branded subdomain is now your public site'
            : 'Branded subdomain is offline — path URL is active again'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const claimedLabel = (organization.subdomain ?? '').trim().toLowerCase();
  const subdomainHostExample = formatOrgSubdomainHost('your-org');
  const claimedSubdomainHost = claimedLabel ? formatOrgSubdomainHost(claimedLabel) : '';
  const brandedUrl = orgPublicBaseUrl(
    { ...organization, subdomain: claimedLabel, subdomainEnabled: true },
    '',
    {},
    'path',
    true
  );
  const pathPublicUrl = slugValue
    ? orgPublicBaseUrl(
        { slug: slugValue, subdomain: undefined, subdomainEnabled: false },
        '',
        {},
        'path',
        false
      )
    : '';
  const savedEnabled = organization.subdomainEnabled ?? false;
  const hasModeChange = mode !== liveMode || (mode === 'subdomain' && enabled !== savedEnabled);
  const labelError = validateLabel(label);
  const canClaim = !labelError && available !== false && !checking;

  const copyPreviewRow = (url: string, copyLabel: string) => (
    <div className="flex flex-wrap items-center gap-2">
      <Input readOnly value={url} className="min-w-[200px] flex-1 font-mono text-sm" />
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          navigator.clipboard?.writeText(url);
          toast.success(copyLabel);
        }}
      >
        Copy
      </Button>
      <Button type="button" variant="outline" onClick={() => window.open(url, '_blank')}>
        Preview
      </Button>
    </div>
  );

  return (
    <AcademySettingsSection
      title="Public site URL"
      divider="none"
      description="Set your public slug and choose how visitors reach your organization page — a path URL on app.courtsync.io or a branded subdomain on Professional+ or Club Pro."
      contentClassName="mt-4"
    >
      <div className="space-y-4">
        <Field data-invalid={!!slugError}>
          <FieldLabel htmlFor="org-slug">Public slug</FieldLabel>
          <Input
            id="org-slug"
            value={slugValue}
            onChange={(e) => onSlugChange(e.target.value)}
            placeholder="your-organization"
          />
          <FieldDescription>
            Used for path-mode URLs: app.courtsync.io/club-academy/{slugValue || 'your-slug'}
          </FieldDescription>
          {slugError ? <FieldError>{slugError}</FieldError> : null}
        </Field>

        <div className="grid gap-4">
          <PublicSiteModeCard
            mode="path"
            selected={mode === 'path'}
            active={liveMode === 'path'}
            title="Path URL"
            description="Visitors reach your site at app.courtsync.io/club-academy/your-slug"
            icon={<Link2 className="h-4 w-4" aria-hidden />}
            onSelect={() => handleModeChange('path')}
          >
            {pathPublicUrl ? copyPreviewRow(pathPublicUrl, 'Path URL copied') : null}
          </PublicSiteModeCard>

          <PublicSiteModeCard
            mode="subdomain"
            selected={mode === 'subdomain'}
            active={liveMode === 'subdomain'}
            disabled={!hasAccess}
            title="Branded subdomain"
            description={`Professional+ — your-org.${subdomainHostExample.split('.').slice(1).join('.')}`}
            icon={<Globe className="h-4 w-4" aria-hidden />}
            badge={
              !hasAccess ? (
                <Badge variant="secondary" className="gap-1">
                  <Lock className="h-3 w-3" aria-hidden />
                  Pro+
                </Badge>
              ) : undefined
            }
            onSelect={() => handleModeChange('subdomain')}
          >
            {!hasAccess ? (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Upgrade to Professional or Club Pro to use a branded subdomain.{' '}
                <Link to="/admin/payments" className="text-primary-600 hover:underline dark:text-primary-400">
                  View plans
                </Link>
              </p>
            ) : isSubdomainClaimed ? (
              <div className="space-y-3">
                <Input readOnly value={claimedSubdomainHost} className="font-mono text-sm" />
                {brandedUrl ? copyPreviewRow(brandedUrl, 'Branded link copied') : null}
              </div>
            ) : (
              <div className="space-y-2">
                <Field>
                  <FieldLabel htmlFor="org-subdomain-label">Subdomain label</FieldLabel>
                  <div className="flex items-center gap-2">
                    <Input
                      id="org-subdomain-label"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      placeholder="your-org"
                      className="font-mono"
                    />
                    <span className="text-sm text-gray-500">.{subdomainHostExample.split('.').slice(1).join('.')}</span>
                  </div>
                  {labelError ? <FieldError>{labelError}</FieldError> : null}
                  {!labelError && checking ? (
                    <FieldDescription>Checking availability…</FieldDescription>
                  ) : null}
                  {!labelError && available === true ? (
                    <FieldDescription className="flex items-center gap-1 text-emerald-600">
                      <Check className="h-3.5 w-3.5" aria-hidden />
                      Available
                    </FieldDescription>
                  ) : null}
                  {!labelError && available === false ? (
                    <FieldError>This subdomain is already in use</FieldError>
                  ) : null}
                </Field>
              </div>
            )}
          </PublicSiteModeCard>
        </div>

        {hasModeChange ? (
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={() => {
                if (mode === 'subdomain' && !isSubdomainClaimed) {
                  setShowClaimConfirm(true);
                } else {
                  void handleSaveMode();
                }
              }}
              disabled={saving || (mode === 'subdomain' && !isSubdomainClaimed && !canClaim)}
              isLoading={saving}
            >
              <Zap className="mr-1.5 h-4 w-4" aria-hidden />
              {mode === 'subdomain' && !isSubdomainClaimed ? 'Claim subdomain' : 'Apply site mode'}
            </Button>
            {!hasAccess && mode === 'subdomain' ? (
              <Link
                to="/admin/payments"
                className="text-sm text-primary-600 hover:underline dark:text-primary-400"
              >
                View plans
              </Link>
            ) : null}
          </div>
        ) : null}
      </div>

      <AlertDialog open={showClaimConfirm} onOpenChange={setShowClaimConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden />
              Claim branded subdomain?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Your public site will move to{' '}
              <strong>{label.trim() ? formatOrgSubdomainHost(label.trim().toLowerCase()) : 'your subdomain'}</strong>.
              Path URLs will redirect visitors to the subdomain host.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
            <Button onClick={() => void handleClaim()} disabled={saving || !canClaim} isLoading={saving}>
              Claim subdomain
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AcademySettingsSection>
  );
}
